import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import type { InputErrorType } from "@b2bportal/core-types";
import { maybeStartFunnel } from "@b2bportal/core-utilities";

import { useI18nContext } from "@hopper-b2b/i18n";
import { CarNavigationUtility, getCarRoutes } from "@tenant/cars";
import dayjs from "dayjs";
import {
  CarsAvailabilityActions,
  CarsAvailabilitySelectors,
} from "../../store/";
import { dateAndTimeToDateTime } from "../../store/utils";
import type { CarLocationSuggestion } from "../CarLocationAutocomplete";
import type { CarSearchComponentProps } from "./CarSearch";
import { DropoffType } from "~core-cars/types";

// TODO these values should be able to be configured tenant by tenant
const MIN_DRIVER_AGE = 16;
const MAX_DRIVER_AGE = 99;
const isDriverAgeValid = (age: number) =>
  !isNaN(age) && age >= MIN_DRIVER_AGE && age <= MAX_DRIVER_AGE;

type FormErrors = {
  pickupDate?: InputErrorType;
  pickupLocation?: InputErrorType;
  dropoffDate?: InputErrorType;
  dropoffLocation?: InputErrorType;
  driverAge?: InputErrorType;
  dropoffTime?: InputErrorType;
  pickupTime?: InputErrorType;
};

export const useCarSearch = ({
  onSubmit,
  onSubmitAppend,
}: CarSearchComponentProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18nContext();

  const [formError, setFormError] = useState<FormErrors>({});

  const pickupDate = useSelector(
    CarsAvailabilitySelectors.getSearchFormPickupDate
  );

  const pickupTime = useSelector(
    CarsAvailabilitySelectors.getSearchFormPickupTime
  );
  const pickupLocation = useSelector(
    CarsAvailabilitySelectors.getSearchFormPickupLocation
  );
  const dropoffType = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffType
  );
  const dropoffDate = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffDate
  );

  const dropoffTime = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffTime
  );

  const dropoffLocation = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffLocation
  );
  const driverAge = useSelector(
    CarsAvailabilitySelectors.getSearchFormDriverAge
  );

  const handleDropoffLocationChange = useCallback(
    (place: CarLocationSuggestion) => {
      dispatch(CarsAvailabilityActions.setNewDropoffLocation(place));
      dispatch(
        CarsAvailabilityActions.setNewDropoffType(
          pickupLocation?.label === place.label
            ? DropoffType.SameLocation
            : DropoffType.DifferentLocation
        )
      );
      setFormError((prev) => {
        return { ...prev, dropoffLocation: undefined };
      });
    },
    [dispatch, pickupLocation?.label]
  );

  const handleDropoffTypeChange = useCallback(
    (dropoffType: DropoffType) => {
      dispatch(CarsAvailabilityActions.setNewDropoffType(dropoffType));
      if (dropoffType === DropoffType.SameLocation && pickupLocation != null) {
        dispatch(CarsAvailabilityActions.setNewDropoffLocation(pickupLocation));
      }
    },
    [dispatch, pickupLocation]
  );

  const handlePickupLocationChange = useCallback(
    (place: CarLocationSuggestion) => {
      dispatch(CarsAvailabilityActions.setNewPickupLocation(place));
      if (dropoffType === DropoffType.SameLocation) {
        dispatch(CarsAvailabilityActions.setNewDropoffLocation(place));
      } else {
        dispatch(
          CarsAvailabilityActions.setNewDropoffType(
            dropoffLocation?.label === place.label
              ? DropoffType.SameLocation
              : DropoffType.DifferentLocation
          )
        );
      }

      setFormError((prev) => {
        return { ...prev, pickupLocation: undefined };
      });
    },
    [dispatch, dropoffLocation?.label, dropoffType]
  );

  const handlePickupDateChange = useCallback(
    (date: Date | undefined) => {
      if (date != null) {
        setFormError((prev) => {
          return { ...prev, pickupDate: undefined };
        });
      }

      dispatch(CarsAvailabilityActions.setNewPickupDate(date?.toISOString()));
    },
    [dispatch]
  );
  const handlePickupTimeChange = useCallback(
    (date: Date | undefined) => {
      dispatch(CarsAvailabilityActions.setNewPickupTime(date?.toISOString()));
    },
    [dispatch]
  );

  const handleDropoffDateChange = useCallback(
    (date: Date | undefined) => {
      if (date != null) {
        setFormError((prev) => {
          return { ...prev, dropoffDate: undefined };
        });
      }
      dispatch(CarsAvailabilityActions.setNewDropoffDate(date?.toISOString()));
    },
    [dispatch]
  );

  const handleDropoffTimeChange = useCallback(
    (date: Date | undefined) => {
      dispatch(CarsAvailabilityActions.setNewDropoffTime(date?.toISOString()));
    },
    [dispatch]
  );

  const handleDriverAgeChange = useCallback(
    (ageStr: string | undefined) => {
      if (ageStr == null || ageStr === "") {
        dispatch(CarsAvailabilityActions.setNewDriverAge(undefined));
      } else {
        const age = Number(ageStr);
        if (!isNaN(age)) {
          setFormError((prev) => {
            return { ...prev, driverAge: undefined };
          });
          dispatch(CarsAvailabilityActions.setNewDriverAge(age));
        }
      }
    },
    [dispatch]
  );

  const isFormValid = useMemo(() => {
    return (
      pickupDate != null &&
      pickupTime != null &&
      pickupLocation != null &&
      dropoffDate != null &&
      dropoffTime != null &&
      dropoffLocation != null &&
      driverAge != null &&
      isDriverAgeValid(driverAge)
    );
  }, [
    driverAge,
    dropoffDate,
    dropoffLocation,
    dropoffTime,
    pickupDate,
    pickupLocation,
    pickupTime,
  ]);

  const handleFormValidation = useCallback(() => {
    const errors: FormErrors = {};

    if (pickupDate == null) errors.pickupDate = { message: undefined };
    if (pickupLocation == null) errors.pickupLocation = { message: undefined };
    if (dropoffDate == null) errors.dropoffDate = { message: undefined };
    if (
      dropoffLocation == null &&
      dropoffType === DropoffType.DifferentLocation
    )
      errors.dropoffLocation = { message: undefined };
    if (pickupTime == null) errors.pickupDate = { message: undefined };
    if (dropoffTime == null) errors.dropoffDate = { message: undefined };

    if (driverAge == null) {
      errors.driverAge = { message: undefined };
    } else if (!isDriverAgeValid(driverAge)) {
      errors.driverAge = { message: t("core-cars.search.driverAgeValidation") };
    }

    setFormError(errors);

    return Object.keys(errors).length === 0;
  }, [
    pickupDate,
    pickupLocation,
    dropoffDate,
    dropoffLocation,
    dropoffType,
    pickupTime,
    dropoffTime,
    driverAge,
    t,
  ]);

  const getAvailabilityPath = useCallback(() => {
    if (
      pickupDate != null &&
      pickupTime != null &&
      pickupLocation != null &&
      dropoffDate != null &&
      dropoffTime != null &&
      dropoffLocation != null &&
      driverAge != null
    ) {
      const searchParams = CarNavigationUtility.buildSearchParams({
        pickupLocation,
        dropoffLocation,
        pickupTime: dayjs(
          dateAndTimeToDateTime(
            pickupDate.toISOString(),
            pickupTime.toISOString()
          )
        ).toDate(),
        dropoffTime: dayjs(
          dateAndTimeToDateTime(
            dropoffDate.toISOString(),
            dropoffTime.toISOString()
          )
        ).toDate(),
        driverAge,
      });

      const { FULL_ROUTES } = getCarRoutes();

      return `${FULL_ROUTES.AVAILABILITY}?${searchParams.toString()}`;
    }

    return null;
  }, [
    driverAge,
    dropoffDate,
    dropoffLocation,
    dropoffTime,
    pickupDate,
    pickupLocation,
    pickupTime,
  ]);

  const handleSearch = useCallback(() => {
    const path = getAvailabilityPath();
    if (handleFormValidation() && path != null) {
      // Rotate the funnel ID when the committed search differs materially
      // from the current one.
      maybeStartFunnel(
        "cars",
        CarNavigationUtility.getCarFunnelSearch(location.search),
        CarNavigationUtility.getCarFunnelSearch(path)
      );

      // Invalidate old search.
      dispatch(CarsAvailabilityActions.resetSearchResults());

      // Navigate
      navigate(path);
    }
  }, [
    getAvailabilityPath,
    handleFormValidation,
    dispatch,
    navigate,
    location.search,
  ]);

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit != null ? onSubmit(event) : handleSearch();
      onSubmitAppend?.();
    },
    [handleSearch, onSubmit, onSubmitAppend]
  );

  return useMemo(
    () => ({
      context: {
        pickupDate,
        pickupTime,
        pickupLocation,
        dropoffDate,
        dropoffTime,
        dropoffLocation,
        driverAge,
        dropoffType,
        isFormValid,
        formError,
      },
      handlers: {
        handlePickupDateChange,
        handlePickupLocationChange,
        handleDropoffTypeChange,
        handlePickupTimeChange,
        handleDropoffDateChange,
        handleDropoffTimeChange,
        handleDropoffLocationChange,
        handleDriverAgeChange,
        onFormSubmit,
        handleSearch,
        getAvailabilityPath,
        setFormError,
      },
    }),
    [
      pickupDate,
      pickupTime,
      pickupLocation,
      dropoffDate,
      dropoffTime,
      dropoffLocation,
      driverAge,
      dropoffType,
      isFormValid,
      formError,
      handlePickupDateChange,
      handlePickupLocationChange,
      handleDropoffTypeChange,
      handlePickupTimeChange,
      handleDropoffDateChange,
      handleDropoffTimeChange,
      handleDropoffLocationChange,
      handleDriverAgeChange,
      onFormSubmit,
      handleSearch,
      getAvailabilityPath,
    ]
  );
};
